/**
 * Debounced auto-save for the Student Model overrides (system prompt, user
 * prompt, JSON schema — persisted as `generative_student_instructions`,
 * `generative_student_prompt` and `generative_schema`). Used by
 * ``ModelSetupPage`` for generative tasks.
 *
 * Mirrors the debounce / flush / unmount behaviour of
 * ``useTeacherPromptAutoSave`` so typing, Next, and fast close all persist
 * the latest value without a separate blur handler.
 */

import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '@/app/providers/globalState';
import {
    normalizeStudentOverrides,
    saveStudentOverridesIfChanged,
    studentOverridesEqual,
    studentOverridesFromUsecase,
} from '@/features/model-setup/utils/saveStudentOverrides';
import { AUTOSAVE_DEBOUNCE_MS } from './useTeacherPromptAutoSave';

/**
 * @param {Object} args
 * @param {Object} args.usecase Current LLMUsecaseInfo row being edited.
 * @param {{systemPrompt: string|null, userPrompt: string|null, schema: string|null}} args.overrides
 *   Controlled override values; `null` means "inherit from teacher".
 * @param {boolean} [args.enabled=true] Gate auto-save.
 * @param {string} [args.role] Auth role; "Viewer" disables saving.
 * @param {(payload: Object) => void} [args.onSaved] Fired after a successful save.
 *
 * @returns {{
 *   autosaving: boolean,
 *   autosaveError: string|null,
 *   isDirty: boolean,
 *   flushSave: () => Promise<{savedSomething: boolean}>,
 *   clearAutosaveError: () => void,
 * }}
 */
export const useStudentOverridesAutoSave = ({
    usecase,
    overrides,
    enabled = true,
    role,
    onSaved,
}) => {
    const { updateUsecase } = useContext(GlobalContext);

    const [autosaving, setAutosaving] = useState(false);
    const [autosaveError, setAutosaveError] = useState(null);

    const lastSavedRef = useRef(normalizeStudentOverrides({}));
    const saveTimerRef = useRef(null);
    const persistInFlightRef = useRef(false);

    const isViewer = role === 'Viewer';

    useEffect(() => {
        if (!enabled) {
            if (saveTimerRef.current != null) {
                clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
                persistSave().catch(() => {});
            }
            setAutosaving(false);
            return;
        }
        lastSavedRef.current = studentOverridesFromUsecase(usecase);
        setAutosaveError(null);
        setAutosaving(false);
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, usecase?._id]);

    const isDirty = !studentOverridesEqual(
        normalizeStudentOverrides(overrides),
        lastSavedRef.current
    );

    const latestRef = useRef({ onSaved, overrides, updateUsecase, usecase });
    latestRef.current = { onSaved, overrides, updateUsecase, usecase };

    const persistSave = useCallback(async () => {
        const {
            onSaved: latestOnSaved,
            overrides: latestOverrides,
            updateUsecase: latestUpdateUsecase,
            usecase: latestUsecase,
        } = latestRef.current;

        if (!latestUsecase?._id) {
            throw new Error('Task not loaded yet');
        }

        const nextValue = normalizeStudentOverrides(latestOverrides);
        if (studentOverridesEqual(nextValue, lastSavedRef.current)) {
            return { savedSomething: false };
        }
        if (persistInFlightRef.current) {
            return { savedSomething: false };
        }
        persistInFlightRef.current = true;

        try {
            const saved = await saveStudentOverridesIfChanged({
                usecase: latestUsecase,
                overrides: latestOverrides,
                comparedTo: lastSavedRef.current,
            });
            if (!saved) {
                lastSavedRef.current = nextValue;
                return { savedSomething: false };
            }

            lastSavedRef.current = nextValue;
            if (typeof latestUpdateUsecase === 'function' && latestUsecase?._id) {
                latestUpdateUsecase(latestUsecase._id, saved);
            }
            if (typeof latestOnSaved === 'function') {
                latestOnSaved({
                    llm_usecase_id: latestUsecase._id,
                    ...saved,
                });
            }
            return { savedSomething: true };
        } finally {
            persistInFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        if (!enabled) return;
        if (isViewer) return;
        if (!usecase?._id || !usecase?.client_id) return;
        if (!isDirty) return;

        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

        let cancelled = false;
        saveTimerRef.current = setTimeout(async () => {
            saveTimerRef.current = null;
            try {
                setAutosaving(true);
                setAutosaveError(null);
                await persistSave();
            } catch (e) {
                if (cancelled) return;
                setAutosaveError(e?.message || 'Failed to save student model settings');
            } finally {
                if (!cancelled) setAutosaving(false);
            }
        }, AUTOSAVE_DEBOUNCE_MS);

        return () => {
            cancelled = true;
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }
        };
    }, [
        overrides?.systemPrompt,
        overrides?.userPrompt,
        overrides?.schema,
        enabled,
        isViewer,
        usecase?._id,
        usecase?.client_id,
        isDirty,
        persistSave,
    ]);

    // Same unmount-flush as ``useTeacherPromptAutoSave``: persist a pending
    // debounce when the Model Configuration tab unmounts.
    useEffect(() => {
        return () => {
            if (saveTimerRef.current == null) return;
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
            persistSave().catch(() => {});
        };
    }, [persistSave]);

    const flushSave = useCallback(async () => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        const nextValue = normalizeStudentOverrides(latestRef.current.overrides);
        if (studentOverridesEqual(nextValue, lastSavedRef.current)) {
            return { savedSomething: false };
        }
        try {
            setAutosaving(true);
            setAutosaveError(null);
            return await persistSave();
        } catch (e) {
            setAutosaveError(e?.message || 'Failed to save student model settings');
            throw e;
        } finally {
            setAutosaving(false);
        }
    }, [persistSave]);

    const clearAutosaveError = useCallback(() => setAutosaveError(null), []);

    return {
        autosaving,
        autosaveError,
        isDirty,
        flushSave,
        clearAutosaveError,
    };
};
